import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { CartProvider } from "./contexts/CartContext";
import { AuthProvider } from "./contexts/AuthContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { CompareProvider } from "./contexts/CompareContext";
import { BestSellerProvider } from "./contexts/BestSellerContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AuthCallback from "./components/AuthCallback";
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ComparePage from "./pages/ComparePage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import LoginPage from "./pages/LoginPage";
import AccountPage from "./pages/AccountPage";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import OrderLookupPage from "./pages/OrderLookupPage";
import FeedbackPage from "./pages/FeedbackPage";
import GoogleCallbackPage from "./pages/GoogleCallbackPage";
import GoogleCompleteProfilePage from "./pages/GoogleCompleteProfilePage";
import AboutPage from "./pages/AboutPage";
import ScentFinderPage from "./pages/ScentFinderPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RefundPage from "./pages/RefundPage";
import ShippingPage from "./pages/ShippingPage";
import ScrollToTop from "./components/ScrollToTop";
import BundlePage from "./pages/BundlePage";
import CustomScentPage from "./pages/CustomScentPage";
import AfterCarePage from "./pages/AfterCarePage";
import AmbassadorDashboardPage from "./pages/AmbassadorDashboardPage";
import ReferralLandingPage from "./pages/ReferralLandingPage";
import ScentQuizPage from "./pages/ScentQuizPage";
import NotFoundPage from "./pages/NotFoundPage";
import FAQPage from "./pages/FAQPage";
import BlogListPage from "./pages/BlogListPage";
import BlogPostPage from "./pages/BlogPostPage";
import AmbassadorApplyPage from "./pages/AmbassadorApplyPage";
import TesterKitCheckoutPage from "./pages/TesterKitCheckoutPage";
import PendingPaymentPage from "./pages/PendingPaymentPage";
import CertificatePage from "./pages/CertificatePage";
import WholesalePage from "./pages/WholesalePage";
import CategoryShopPage from "./pages/CategoryShopPage";
import CookieConsent from "./components/CookieConsent";
import PushNotificationPrompt from "./components/PushNotificationPrompt";
import CustomCursor from "./components/CustomCursor";
import ExitIntentPopup from "./components/ExitIntentPopup";
import FloatingHelpButton from "./components/FloatingHelpButton";
import CompareWidget from "./components/CompareWidget";
import LuxeBackdrop from "./components/LuxeBackdrop";
import Sparkles from "./components/Sparkles";
import FloatingAmbassadorCTA from "./components/FloatingAmbassadorCTA";
import { trackPageView, startHeartbeat } from "./utils/analytics";
import { registerServiceWorker } from "./utils/pwa";

function AppRouter() {
  const location = useLocation();

  // Track page views on route change
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);

  // Live-now heartbeat (mounted once)
  useEffect(() => {
    startHeartbeat();
  }, []);

  // Hide luxe backdrop on admin / utility pages — keep workspace clean
  useEffect(() => {
    const path = location.pathname || '';
    const hide = /^\/admin/.test(path);
    if (hide) document.body.setAttribute('data-no-backdrop', '1');
    else document.body.removeAttribute('data-no-backdrop');
  }, [location.pathname]);

  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            <Routes location={location}>
              <Route path="/" element={<HomePage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/shop/:slug" element={<CategoryShopPage />} />
              <Route path="/product/:id" element={<ProductPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmationPage />} />
              <Route path="/track-order" element={<OrderLookupPage />} />
              <Route path="/reviews" element={<FeedbackPage />} />
              <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
              <Route path="/complete-profile-google-login" element={<GoogleCompleteProfilePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/account/complete-profile" element={<CompleteProfilePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/scent-finder" element={<ScentFinderPage />} />
              <Route path="/scent-quiz" element={<ScentQuizPage />} />
              <Route path="/bundle" element={<BundlePage />} />
              <Route path="/custom-scent" element={<CustomScentPage />} />
              <Route path="/after-care" element={<AfterCarePage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/ambassador-apply" element={<AmbassadorApplyPage />} />
              <Route path="/become-ambassador" element={<AmbassadorApplyPage />} />
              <Route path="/become-distributor" element={<AmbassadorApplyPage />} />
              <Route path="/tester-kit-checkout" element={<TesterKitCheckoutPage />} />
              <Route path="/pending-payment" element={<PendingPaymentPage />} />
              <Route path="/certificate/:year/:month" element={<CertificatePage />} />
              <Route path="/wholesale" element={<WholesalePage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/refund-policy" element={<RefundPage />} />
              <Route path="/shipping-policy" element={<ShippingPage />} />
              <Route path="/admin" element={<AdminLoginPage />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/ambassador" element={<AmbassadorDashboardPage />} />
              <Route path="/r/:slug" element={<ReferralLandingPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </main>
      <Routes>
        <Route path="/admin/*" element={null} />
        <Route path="/login" element={null} />
        <Route path="/forgot-password" element={null} />
        <Route path="/account/complete-profile" element={null} />
        <Route path="*" element={<Footer />} />
      </Routes>
      <FloatingAmbassadorCTA />
    </>
  );
}

function App() {
  useEffect(() => {
    // Register service worker once on mount (idempotent)
    registerServiceWorker();
  }, []);

  return (
    <AuthProvider>
      <CurrencyProvider>
        <CartProvider>
          <CompareProvider>
          <BestSellerProvider>
          <BrowserRouter>
            <Toaster
              position="top-center"
              toastOptions={{
                style: {
                  background: '#141414',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.1)',
                  fontFamily: 'Outfit, sans-serif',
                },
              }}
            />
            <CustomCursor />
            <LuxeBackdrop />
            <Sparkles />
            <div className="flex flex-col min-h-screen text-white">
              <AppRouter />
              <CookieConsent />
              <PushNotificationPrompt />
              <ExitIntentPopup />
              <FloatingHelpButton />
              <CompareWidget />
            </div>
          </BrowserRouter>
          </BestSellerProvider>
          </CompareProvider>
        </CartProvider>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
